/**
 * Card number input used for adding a card
 *
 * Dependent on getCardSpecs() from https://github.paypal.com/ConsumerWeb-Modules/consumerweb-card/blob/master/lib/card-config.js,
 * but you could just supply an object of specifications similar to what getCardSpecs returns.
 */
import React from 'react'
import { TextInput } from '@paypalcorp/pp-react';
import PropTypes from 'prop-types'

import { cardSpecsPropType, cardSpecPropType } from 'prop-validators'
import { defaultCardSpec, getSpecificCardSpec } from './card-utils'

const STYLES = {
  logoWrapper: {
    height: '100%',
    position: 'absolute',
    top: 0,
    right: 0,
  },

  logo: {
    display: 'inline-block',
    marginTop: 12,
    marginRight: 2,
  },

  errorMessage: {
    WebkitTransition: 'all 0.25s ease-in-out',
    MozTransition: 'all 0.25s ease-in-out',
    msTransition: 'all 0.25s ease-in-out',
    OTransition: 'all 0.25s ease-in-out',
    transition: 'all 0.25s ease-in-out',
  },
}

class CardNumber extends React.Component {
  static propTypes = {
    cardSpecs: cardSpecsPropType,
    selectedCardSpec: cardSpecPropType,
    label: PropTypes.string.isRequired,
    errorMessage: PropTypes.string,
    handleChangeCardNumber: PropTypes.func.isRequired,
    handleFocus: PropTypes.func,
    handleBlur: PropTypes.func,
    dir: PropTypes.string,
  }

  static defaultProps = {
    handleFocus: () => {},
    handleBlur: () => {},
  }

  state = {
    value: '',
    cardSpec: defaultCardSpec,
    showIsRequiredError: false,
  }

  /**
   * Handle changing card number
   * Get the specific card spec for the card based on the card number pattern
   * Call parent's handleChangeCardNumber, so it knows what the card spec is
   * @param {String} cardNumber - the card number
   * @return {String} cardNumber - the sanitized card number
   */
  handleChangeCardNumber = ({ target: { value: cardNumber } }) => {
    // the cardSpecs for all the cards from consumerweb-card's card-config
    // https://github.paypal.com/ConsumerWeb-Modules/consumerweb-card/blob/master/lib/card-config.js#L73
    let { cardSpecs } = this.props
    cardNumber = cardNumber.replace(/\D+/g, '')

    // get the specific cardSpec based on the cardNumber using the autodetect from cardSpecs
    let cardSpec =
      (cardSpecs.autodetect && getSpecificCardSpec(cardSpecs, cardNumber)) ||
      this.props.selectedCardSpec ||
      defaultCardSpec
    this.setState({ cardSpec, value: cardNumber })
    this.handleCheckForValue(cardNumber)
    this.props.handleChangeCardNumber(cardNumber, cardSpec)
    return cardNumber
  }

  /**
   * Check for a cardNumber value
   * If the field is required, we should show that this field needs a value
   * @param {String} cardNumber - the card number
   */
  handleCheckForValue = cardNumber => {
    const cardNumberSpecs = this.state.cardSpec.fields.ccNumber
    const isRequired = cardNumberSpecs.required
    this.setState({ showIsRequiredError: isRequired && !cardNumber })
  }

  /**
   * Call parent's handleFocus
   * @param {String} cardNumber - the card number
   */
  handleFocus = ({ target: { value: cardNumber } }) => {
    this.props.handleFocus(cardNumber)
  }

  /**
   * Check for a cardNumber value and call parent's handleBlur
   * @param {String} cardNumber - the card number
   */
  handleBlur = ({ target: { value: cardNumber } }) => {
    this.handleCheckForValue(cardNumber)
    this.props.handleBlur(cardNumber)
  }

  render() {
    const {
      errorMessage,
      cardSpecs: { autodetect = false },
    } = this.props
    const cardNumberSpecs = this.state.cardSpec.fields.ccNumber

    // the card type from config separates words with a '_',
    // but we don't want '_'s so we can use the font icons
    const cardType = autodetect ?
      this.state.cardSpec.type.replace('_', '') :
      this.props.selectedCardSpec.type.replace('_', '')
    const cardLogoClassNames =
      !(errorMessage || this.state.showIsRequiredError) ?
      `icon icon-medium icon-${cardType}-logo` :
      undefined;

    if (this.props.dir === 'rtl') {
      delete STYLES.logoWrapper.right
      STYLES.logoWrapper = { ...STYLES.logoWrapper, left: 0 }

      delete STYLES.logo.marginRight
      STYLES.logo = { ...STYLES.logo, marginLeft: 2 }
    }

    return (
		<div
			style={{ margin: '10px 0 0', position: 'relative' }}
		>
			<TextInput
				type="tel"
				value={this.state.value}
				id="cardNumber"
				name="cardNumber"
				label={this.props.label}
				maxLength={cardNumberSpecs.maxlength}
				pattern={cardNumberSpecs.pattern || undefined}
				required={cardNumberSpecs.required}
				onChange={this.handleChangeCardNumber}
				onFocus={this.handleFocus}
				onBlur={this.handleBlur}
				errorText={errorMessage}
				dir={this.props.dir}
	        />

	        <span style={STYLES.logoWrapper}>
	          <span className={cardLogoClassNames} style={STYLES.logo} />
	        </span>

      </div>
    )
  }
}

export { defaultCardSpec }
export default CardNumber